// Перелистывание месяца. Одно на весь продукт.
//
// До 25.08.2026 их было четыре, и все разные: в ведомости стрелки стояли текстовыми знаками
// «←» и «→» в кнопках размера xxsmall с подписью «Месяц ведомости»; в табеле те же знаки, но
// размером xsmall и без подписи; в расходе вместо стрелок стояли слова «Раньше» и «Позже»
// ссылками; в карточке объекта — настоящие иконки-стрелки. Человек, переходящий из табеля в
// ведомость, каждый раз заново искал, чем тут листают месяц, и это не придирка: соседние
// экраны одного раздела выглядели как куски разных программ.
//
// Ширина названия месяца ЗАКРЕПЛЕНА. «Май 2026» короче, чем «Сентябрь 2026», и без закрепления
// стрелки прыгали бы под курсором при каждом переключении — попасть по «дальше» дважды подряд
// становилось делом удачи.

import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import * as Label from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/screen-state';

export interface PeriodStepperProps {
  /** Подпись над календарём: «Месяц ведомости», «Месяц табеля». Без неё календарь безымянный. */
  label?: string;
  /**
   * Название месяца словами и годом: «Май 2026». Правило 9: календарь подписан датой.
   *
   * `null` — месяц ещё неизвестен: экран открыт без месяца в адресе, и какой из них покажут,
   * решит ответ сервера («последний, где есть данные»). Вместо названия стоит заглушка: текущий
   * месяц, подставленный «пока едет», на ответе сменился бы на другой, и за это время календарь
   * успел бы соврать.
   */
  title: string | null;
  onPrev: () => void;
  onNext: () => void;
  /** Запрет шага вперёд: дальше текущего месяца ведомостей не бывает. */
  nextDisabledReason?: string;
  /**
   * Чей это календарь, для чтения с экрана: «начала периода», «конца периода».
   *
   * Когда на экране два календаря (границы одного периода), у обоих стрелки назывались бы одинаково
   * — «Предыдущий месяц», «Следующий месяц», — и по голосу было бы не понять, какую из них нажимать.
   * Без параметра имена прежние: остальные экраны на него не рассчитывают.
   */
  ariaName?: string;
}

export function PeriodStepper({
  label,
  title,
  onPrev,
  onNext,
  nextDisabledReason,
  ariaName,
}: PeriodStepperProps) {
  const чей = ariaName === undefined ? '' : `, ${ariaName}`;
  return (
    <div className='flex flex-col gap-1'>
      {/* Подпись тем же примитивом, что у поля выбора рядом: иначе календарь и отбор в одной
          шапке стоят на разных базовых линиях. */}
      {label !== undefined && <Label.Root>{label}</Label.Root>}
      <div className='flex min-h-9 items-center gap-1'>
        <Button.Root
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={onPrev}
          aria-label={`Предыдущий месяц${чей}`}
        >
          <Button.Icon as={RiArrowLeftSLine} />
        </Button.Root>
        {title === null ? (
          // Ширина та же, что у названия: стрелки не сдвинутся, когда месяц станет известен.
          <span
            role='status'
            aria-label='Определяем месяц'
            className='flex min-w-period items-center justify-center'
          >
            <Skeleton className='h-4 w-24' />
          </span>
        ) : (
          <span className='min-w-period text-center text-label-sm tabular-nums text-text-strong-950'>
            {title}
          </span>
        )}
        <Button.Root
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={onNext}
          aria-label={
            nextDisabledReason === undefined
              ? `Следующий месяц${чей}`
              : `Следующий месяц${чей}. ${nextDisabledReason}`
          }
        >
          <Button.Icon as={RiArrowRightSLine} />
        </Button.Root>
      </div>
    </div>
  );
}
